import {isValid} from 'date-fns';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import type {AbcXyzAnalysisDetailResponse} from '@dms/api/reporting';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish, parseDate} from 'shared/web';

import {SPARSE_HISTORY_ACTIVE_MONTHS_RATIO} from '../constants/SPARSE_HISTORY_ACTIVE_MONTHS_RATIO';
import type {AbcXyzMetric} from '../types/AbcXyzMetric';
import type {AbcXyzThreshold} from '../types/AbcXyzThreshold';

const MONEY_DECIMALS = 2;
const COUNT_DECIMALS = 0;
const DECIMAL_DECIMALS = 2;
/** Separates the two ends of a period or a value range, which reads the same in every language. */
const RANGE_SEPARATOR = ' – ';
const ABOVE_PREFIX = '> ';
const AT_MOST_PREFIX = '≤ ';
const AT_LEAST_PREFIX = '≥ ';
const CONDITION_SEPARATOR = ' · ';
const CORRELATION_SYMBOL = 'r';
const LOWER_BOUND = '0';
const PERCENT_SIGN = ' %';
const FULL_SHARE = '100';
/** The ABC thresholds arrive as fractions of the total (0.8), the table shows percent. */
const PERCENT_FACTOR = 100;
const RATIO_SEPARATOR = ' / ';

const abcClassName = (abcClass: string | Nullish) =>
  match(abcClass)
    .with('A', () => i18n.t('entity.warehouse.reporting.highValueItems'))
    .with('B', () => i18n.t('entity.warehouse.reporting.mediumValueItems'))
    .with('C', () => i18n.t('entity.warehouse.reporting.lowValueItems'))
    .otherwise(() => null);

const xyzClassName = (xyzClass: string | Nullish) =>
  match(xyzClass)
    .with('X', () => i18n.t('entity.warehouse.reporting.stableDemand'))
    .with('Y', () => i18n.t('entity.warehouse.reporting.variableDemand'))
    .with('Z', () => i18n.t('entity.warehouse.reporting.irregularDemand'))
    .otherwise(() => null);

/**
 * Turns the row detail into display values. Classes, metrics and thresholds are shown as the API
 * returns them — nothing here classifies or recalculates; it only formats.
 */
export function useAbcXyzDetailView(detail: AbcXyzAnalysisDetailResponse) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();
  // The amounts are in the tenant currency. A failing tenant query only costs the currency symbol.
  const {data: tenant} = useGetTenantQuery();

  const decimal = (value: number | Nullish) =>
    formatNumber(value, DECIMAL_DECIMALS) ?? EMPTY_PLACEHOLDER;
  const count = (value: number | Nullish) =>
    formatNumber(value, COUNT_DECIMALS) ?? EMPTY_PLACEHOLDER;
  // Trailing zeros only get in the way of a threshold such as 80 %.
  const trimmed = (value: number | Nullish) =>
    isNotNil(value) && Number.isInteger(value) ? count(value) : decimal(value);
  const fractionAsPercent = (value: number | Nullish) =>
    trimmed(isNil(value) ? value : value * PERCENT_FACTOR);
  const percent = (value: number | Nullish) =>
    isNil(value) ? EMPTY_PLACEHOLDER : `${decimal(value)} %`;
  const money = (value: number | Nullish) =>
    isNil(value) || isNil(tenant?.currency)
      ? decimal(value)
      : (formatCurrency(value, tenant.currency, MONEY_DECIMALS) ?? EMPTY_PLACEHOLDER);
  const date = (value: string | Nullish) => {
    const parsed = isNotNil(value) ? parseDate(value) : null;

    return isNotNil(parsed) && isValid(parsed)
      ? formatDateTime('dateShort', parsed)
      : EMPTY_PLACEHOLDER;
  };
  const period = (from: string | Nullish, to: string | Nullish) =>
    [date(from), date(to)].join(RANGE_SEPARATOR);
  const months = (value: number | Nullish) =>
    isNil(value)
      ? EMPTY_PLACEHOLDER
      : i18n.t('entity.warehouse.reporting.historyLengthMonths', {count: count(value)});

  const abcMetrics: AbcXyzMetric[] = [
    {
      id: 'netConsumptionValue',
      label: i18n.t('entity.warehouse.reporting.netConsumptionValue'),
      value: money(detail.abcNetConsumptionValue),
    },
    {
      id: 'shareOfTotal',
      label: i18n.t('entity.warehouse.reporting.shareOfTotal'),
      value: percent(detail.abcSharePercent),
    },
    {
      id: 'cumulativeShare',
      label: i18n.t('entity.warehouse.reporting.cumulativeShare'),
      value: percent(detail.abcCumulativeSharePercent),
    },
    {
      id: 'abcRank',
      label: i18n.t('entity.warehouse.reporting.abcRank'),
      value: count(detail.abcRank),
    },
    {
      id: 'evaluationPeriod',
      label: i18n.t('entity.warehouse.reporting.evaluationPeriod'),
      value: period(detail.abcPeriodStart, detail.abcPeriodEnd),
    },
    {
      id: 'historyLength',
      label: i18n.t('entity.warehouse.reporting.historyLength'),
      value: months(detail.abcHistoryMonthCount),
    },
  ];

  const meanQuantity = isNil(detail.meanMonthlyQuantity)
    ? EMPTY_PLACEHOLDER
    : [decimal(detail.meanMonthlyQuantity), detail.unitName].filter(isNotNil).join(' ');
  const seasonality = match(detail.seasonalityDetected)
    .with(true, () => i18n.t('entity.warehouse.reporting.seasonalityDetected'))
    .with(false, () => i18n.t('entity.warehouse.reporting.seasonalityNotDetected'))
    .otherwise(() => EMPTY_PLACEHOLDER);

  const xyzMetrics: AbcXyzMetric[] = [
    {
      id: 'meanMonthlyQuantity',
      label: i18n.t('entity.warehouse.reporting.meanMonthlyIssuedQuantity'),
      value: meanQuantity,
    },
    {
      id: 'coefficientOfVariation',
      label: i18n.t('entity.warehouse.reporting.coefficientOfVariation'),
      value: decimal(detail.coefficientOfVariation),
    },
    {
      id: 'activeMonths',
      label: i18n.t('entity.warehouse.reporting.activeMonths'),
      value: [count(detail.activeMonthCount), count(detail.xyzHistoryMonthCount)].join(
        RATIO_SEPARATOR
      ),
    },
    {
      id: 'seasonality',
      label: i18n.t('entity.warehouse.reporting.seasonality'),
      value: seasonality,
    },
    {
      id: 'evaluationPeriod',
      label: i18n.t('entity.warehouse.reporting.evaluationPeriod'),
      value: period(detail.xyzPeriodStart, detail.xyzPeriodEnd),
    },
    {
      id: 'historyLength',
      label: i18n.t('entity.warehouse.reporting.historyLength'),
      value: months(detail.xyzHistoryMonthCount),
    },
  ];

  const abcThresholds: AbcXyzThreshold[] = [
    {
      id: 'A',
      className: 'A',
      range: `${[LOWER_BOUND, fractionAsPercent(detail.thresholdA)].join(RANGE_SEPARATOR)}${PERCENT_SIGN}`,
      description: i18n.t('entity.warehouse.reporting.highValueItems'),
    },
    {
      id: 'B',
      className: 'B',
      range: `${ABOVE_PREFIX}${[
        fractionAsPercent(detail.thresholdA),
        fractionAsPercent(detail.thresholdB),
      ].join(RANGE_SEPARATOR)}${PERCENT_SIGN}`,
      description: i18n.t('entity.warehouse.reporting.mediumValueItems'),
    },
    {
      id: 'C',
      className: 'C',
      range: `${ABOVE_PREFIX}${[fractionAsPercent(detail.thresholdB), FULL_SHARE].join(RANGE_SEPARATOR)}${PERCENT_SIGN}`,
      description: i18n.t('entity.warehouse.reporting.lowValueItems'),
    },
  ];

  // xyz-v1: X when the coefficient of variation is at most the first threshold, Y when it is above
  // it and the seasonality correlation reaches the second one, Z otherwise (no threshold of its own).
  const xyzThresholds: AbcXyzThreshold[] = [
    {
      id: 'X',
      className: 'X',
      range: `${AT_MOST_PREFIX}${decimal(detail.thresholdCvX)}`,
      description: i18n.t('entity.warehouse.reporting.stableDemand'),
    },
    {
      id: 'Y',
      className: 'Y',
      range: `${ABOVE_PREFIX}${decimal(detail.thresholdCvX)}${CONDITION_SEPARATOR}${CORRELATION_SYMBOL} ${AT_LEAST_PREFIX}${decimal(detail.thresholdRY)}`,
      description: i18n.t('entity.warehouse.reporting.variableDemand'),
    },
    {
      id: 'Z',
      className: 'Z',
      range: EMPTY_PLACEHOLDER,
      description: i18n.t('entity.warehouse.reporting.irregularDemand'),
    },
  ];

  const abcName = abcClassName(detail.abcClass);
  const xyzName = xyzClassName(detail.xyzClass);
  const hasSparseHistory =
    isNotNil(detail.activeMonthCount) &&
    isNotNil(detail.xyzHistoryMonthCount) &&
    detail.xyzHistoryMonthCount > 0 &&
    detail.activeMonthCount / detail.xyzHistoryMonthCount <= SPARSE_HISTORY_ACTIVE_MONTHS_RATIO;
  const dataQualityMessage =
    detail.insufficientHistory === true || hasSparseHistory
      ? i18n.t('entity.warehouse.reporting.dataQualityWarningMessage', {
          active: count(detail.activeMonthCount),
          total: count(detail.xyzHistoryMonthCount),
        })
      : null;

  return {
    abcName,
    xyzName,
    combinedName: [abcName, xyzName].filter(isNotNil).join(CONDITION_SEPARATOR) || null,
    dataQualityMessage,
    abcHint: i18n.t('entity.warehouse.reporting.valueShareHint', {
      share: percent(detail.abcSharePercent),
    }),
    xyzHint: i18n.t('entity.warehouse.reporting.coefficientOfVariationHint', {
      value: decimal(detail.coefficientOfVariation),
    }),
    abcMetrics,
    xyzMetrics,
    abcThresholds,
    xyzThresholds,
  };
}
